import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  type LucideIcon,
} from 'lucide-react';

type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  title?: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  actions?: ReactNode;
}

const variantClasses: Record<AlertVariant, { container: string; icon: string; title: string; body: string; close: string }> = {
  info: {
    container: 'bg-info-50 border-info-100 text-info-700',
    icon: 'text-info-600',
    title: 'text-info-700',
    body: 'text-info-700/90',
    close: 'text-info-600 hover:bg-info-100',
  },
  success: {
    container: 'bg-success-50 border-success-100 text-success-700',
    icon: 'text-success-600',
    title: 'text-success-700',
    body: 'text-success-700/90',
    close: 'text-success-600 hover:bg-success-100',
  },
  warning: {
    container: 'bg-warning-50 border-warning-100 text-warning-700',
    icon: 'text-warning-600',
    title: 'text-warning-700',
    body: 'text-warning-700/90',
    close: 'text-warning-600 hover:bg-warning-100',
  },
  danger: {
    container: 'bg-danger-50 border-danger-100 text-danger-700',
    icon: 'text-danger-600',
    title: 'text-danger-700',
    body: 'text-danger-700/90',
    close: 'text-danger-600 hover:bg-danger-100',
  },
};

const variantIcons: Record<AlertVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ variant = 'info', title, icon, onDismiss, actions, className = '', children, ...props }, ref) => {
    const styles = variantClasses[variant];
    const IconComponent = variantIcons[variant];

    return (
      <div
        ref={ref}
        role="alert"
        className={[
          'flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
          styles.container,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        <span className={`flex-shrink-0 mt-0.5 ${styles.icon}`} aria-hidden="true">
          {icon ?? <IconComponent className="w-5 h-5" />}
        </span>
        <div className="flex-1 min-w-0">
          {title && <div className={`font-semibold mb-0.5 ${styles.title}`}>{title}</div>}
          {children && <div className={styles.body}>{children}</div>}
          {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className={`flex-shrink-0 rounded-md p-1 transition-colors ${styles.close}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  },
);

Alert.displayName = 'Alert';
